import React from 'react'
import { AdCreative } from '@marketing-milk/interfaces'
import { Tooltip } from '@material-ui/core'
import { CreativeMedia } from './CreativeMedia'
import { CreativeVerdictBadge, hasClientApproval } from './CreativeVerdictBadge'

type Props = {
  creative: AdCreative
  selectedText?: string
  highlighted?: boolean
  /** Dims the row and blocks the click outright. */
  disabled?: boolean
  /**
   * Why this ad is withheld from being picked, named on the row. Separate from
   * `disabled` because a withheld ad that is already attached still has to be
   * clickable — see `CreativeCard`.
   */
  withheldReason?: string
  onSelect: (adCreative: AdCreative) => void
}

export const CreativeListRow = ({
  creative,
  selectedText,
  highlighted,
  disabled,
  withheldReason,
  onSelect,
}: Props) => {
  const selected = Boolean(selectedText)

  return (
    <div
      className={`flex items-center gap-4 w-full px-4 py-2 border-l-4 transition-colors duration-150 ${
        disabled || withheldReason ? 'opacity-50' : ''
      } ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'} ${
        selected
          ? 'bg-purple-50 border-l-purple-600'
          : highlighted
          ? 'bg-amber-50 border-l-amber-400'
          : 'border-l-transparent hover:bg-gray-50'
      }`}
      data-testid={creative.name}
      onClick={() => !disabled && onSelect(creative)}
    >
      <div className="w-14 h-14 flex-shrink-0 rounded-md overflow-hidden">
        <CreativeMedia
          creative={creative}
          className="h-14 w-14 bg-gray-200 text-[6px] leading-none text-center px-0.5"
        />
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-900 truncate">{creative.name}</p>
        <p className="text-sm text-gray-500 truncate">{creative.linkHeadline || '—'}</p>
        {withheldReason ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-800">
            <i className="fa fa-ban" aria-hidden="true" />
            {withheldReason}
          </span>
        ) : (
          hasClientApproval(creative) && <CreativeVerdictBadge />
        )}
      </div>

      {/* Independent of the active rail's highlight — that only marks ads matching whichever
          form is selected. This shows what's actually attached right now, so an ad already
          carrying a *different* form doesn't look identical to a truly unassigned one. */}
      <div className="flex-shrink-0 w-6 flex justify-center">
        {creative.leadCardUsed && (
          <Tooltip title={`Lead form: ${creative.leadCardUsed.formName}`}>
            <i data-testid="lead-card-icon" className="fad fa-address-card text-purple-500" />
          </Tooltip>
        )}
      </div>

      <div className="flex-shrink-0 w-24 text-right">
        {disabled ? (
          <span className="text-sm font-semibold text-gray-500">Published</span>
        ) : selected ? (
          <span className="text-sm font-semibold text-purple-600">{selectedText}</span>
        ) : (
          <span className="text-sm text-gray-400">Select</span>
        )}
      </div>
    </div>
  )
}
